import type { Fragment } from "../types.js";
import { colors, radii, shadows } from "../../brand/theme.js";
import { tl } from "../timeline.js";

type Variant = "filled" | "ghost" | "outline";

/**
 * Card — slide-up + fade-in container with backdrop blur. Mirrors
 * `src/primitives/Card.tsx` (spring damping=18 over 22 frames at 30fps ≈ 0.73s).
 *
 * `childrenHtml` is inlined as-is. Children may carry their own GSAP IDs.
 */
export function card(opts: {
  id: string;
  childrenHtml: string;
  startSec: number;
  delaySec?: number;
  variant?: Variant;
  padding?: number;
  width?: number | string;
}): Fragment {
  const {
    id,
    childrenHtml,
    startSec,
    delaySec = 0,
    variant = "filled",
    padding = 48,
    width = "100%",
  } = opts;
  const widthCss = typeof width === "number" ? `${width}px` : width;

  const variantStyles: Record<Variant, string> = {
    filled: `background:rgba(255,255,255,0.06);border:1px solid ${colors.primary}33;`,
    ghost: `background:rgba(255,255,255,0.02);border:1px solid ${colors.muted}22;`,
    outline: `background:transparent;border:2px solid ${colors.primary};`,
  };

  const html = `<div id="${id}" class="card" style="width:${widthCss};padding:${padding}px;border-radius:${radii.lg}px;backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);box-shadow:${shadows.card};${variantStyles[variant]}">
${childrenHtml}
</div>`;

  const at = startSec + delaySec;
  const tweens = [
    tl.fromTo(
      `#${id}`,
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
      at,
    ),
  ];
  const init = `gsap.set("#${id}", { opacity: 0, y: 40 });`;
  return { html, init, tweens };
}
